import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Formats, IMeetingConfirmation, IUser, UserRemoteWorkTypes,
  UserStatusMarkers, setClass, useStore
} from '@utils';
import { isMobile } from 'react-device-detect';
import {
  AddMembersIcon, Button, Plus,
} from '@components';
import { MemberList } from '../../../../meetingPlanerSchedule/components';
import { MembersChipList } from './MembersChipList';
import { MembersStore } from "../../../../../store";

interface MembersBlockProp {
  meetingMembers: IUser[];
  initiatorId?: number;
  isEdit: boolean;
  format: string | undefined;
  confirmations?: IMeetingConfirmation[];
  onDeleteMember: (memberId: number) => void;
  onClickMember: (memberid: number) => void;
  handleMobileMembersSelect: () => void;
}

export const MembersBlock: FC<MembersBlockProp> = ({
  meetingMembers, initiatorId, onDeleteMember, onClickMember, 
  isEdit, format, confirmations, handleMobileMembersSelect,
}) => {
  const membersStore = useStore(MembersStore);
  const [listOpen, setListOpen] = useState(false);
  const [isOpenMemberList, setIsOpenMemberList] = useState(false);
  const listRef = useRef<HTMLDivElement>();

  const getMembersRows = () => {
    if (meetingMembers.length === 0) {
      return 1;
    }
    const rows = Math.ceil(meetingMembers.length / 2);
    return !listOpen && rows > 3 ? 3 : rows;
  };

  useEffect(() => {
    if (meetingMembers.length < 6 && listOpen) {
      setListOpen(false);
    }
  }, [meetingMembers.length]);

  const topOfList = window.scrollY + 4 + (32 * getMembersRows());
  const rightOfList = window.innerWidth - listRef.current?.getBoundingClientRect().right - 116;
  const findById = (id: number) => meetingMembers.find((user) => user.id === id);
  const sortingRule = (a: IUser, b: IUser) => {
    if (findById(a.id) && !findById(b.id)) {
      return -1;
    }
    if (findById(b.id) && !findById(a.id)) {
      return 1;
    }
    if (a.fullName < b.fullName) {
      return -1;
    }
    if (a.fullName > b.fullName) {
      return 1;
    }
    return 0;
  };

  const switchMemberList = () => {
    if (!isMobile) {
      setIsOpenMemberList((prev) => !prev);
    } else {
      handleMobileMembersSelect();
    }
  };
  const switchMembersChipList = () => setListOpen((prev) => !prev);
  
  const showRemoteUserWarning = () => {
    return format === Formats.OFFLINE && meetingMembers.filter((member) => {
      return member.remoteWorkType === UserRemoteWorkTypes.REMOTE || member.remoteWorkType === UserRemoteWorkTypes.CAN_COME
        || (membersStore.membersStatus.get(member.id)
          && membersStore.membersStatus.get(member.id)[0].marker === UserStatusMarkers.REMOTE_WORK);
    }).length > 0;
  };
  
  return (
    <div className="area members-area">
      {(meetingMembers.length > 0 || isEdit) && (
        <AddMembersIcon divClassName="members-icon" />
      )}
      <div className={setClass("members", isMobile && 'mobile')}>
        <div 
          ref={listRef} 
          className={setClass('list', isMobile && 'mobile', listOpen ? 'open-list' : 'closed-list')}
        >
          {meetingMembers.length === 0 && !isOpenMemberList && isEdit && (
            <Button
              className="add-members-button outlined-borderless"
              title="Добавить участников"
              onClick={switchMemberList}
            />
          )}
          <MembersChipList
            readOnly={isEdit}
            meetingMembers={meetingMembers}
            initiatorId={initiatorId}
            listOpen={listOpen}
            setListOpen={setListOpen}
            onDeleteMember={onDeleteMember}
            confirmations={confirmations}
          />
          {!listOpen && meetingMembers.length > 5 && (
            <Button
              className="open-members-list-button"
              title={`+${meetingMembers.length - 5}`}
              onClick={switchMembersChipList}
            />
          )}
        </div>
        {listOpen && (
          <div className="close-list-button-container">
            <Button
              className="close-members-list-button outlined-borderless"
              title="Свернуть"
              onClick={switchMembersChipList}
            />
          </div>
        )}
        {isEdit && meetingMembers.length > 0 && (
          <Button
            className="add-members-button outlined-borderless"
            icon={<Plus divClassName="add-members-icon" />}
            title="Добавить участников"
            onClick={switchMemberList}
          />
        )}
        {showRemoteUserWarning() && (
          <div className="remote-user-warning">
            В списке сотрудников для офлайн встречи присутствуют сотрудники на удаленной работе.
          </div>
        )}
      </div>
      {
        isOpenMemberList && (
          <MemberList
            topOfList={topOfList}
            onClose={switchMemberList}
            editMeeting
            onMemberSelect={onClickMember}
            isCanResetChoosedOption={!!meetingMembers.length}
            sortingRule={sortingRule}
          />
        )
      }
    </div>
  );
};
